// External dependencies
import React, { useEffect, useState } from 'react';

import * as api from '../api/admin-reference-threads.api';
import UserLink from './UserLink.component';
import AdminHeader from './AdminHeader.component';
import {
  formatAdminDateTime,
  getAdminDateTimeAttribute,
} from '../utils/admin-date';
import { getAdminMessagesHref } from '../utils/member-url';
import type {
  ReferenceThread,
  ReferenceUserOrId,
  TopNegativeRecipient,
} from '../api/admin-reference-threads.api';

function ReferenceUser({ user }: { user?: ReferenceUserOrId }) {
  if (user && typeof user !== 'string') {
    return <UserLink user={user} />;
  }
  return <em>Unknown member</em>;
}

/**
 * Lists threads that received negative references
 */
export default function AdminReferenceThreads() {
  const [isFetching, setIsFetching] = useState(false);
  const [referenceThreads, setReferenceThreads] = useState<ReferenceThread[]>(
    [],
  );
  const [topNegativeRecipients, setTopNegativeRecipients] = useState<
    TopNegativeRecipient[]
  >([]);

  async function fetchReferenceThreads() {
    setIsFetching(true);
    try {
      const results = await api.getReferenceThreads();
      setReferenceThreads(Array.isArray(results) ? results : results.items);
      setTopNegativeRecipients(
        Array.isArray(results) ? [] : results.topNegativeRecipients || [],
      );
    } catch {
      // eslint-disable-next-line no-console
      console.log(`Could not load reference threads`);
    } finally {
      setIsFetching(false);
    }
  }

  useEffect(() => {
    fetchReferenceThreads();
  }, []);

  return (
    <>
      <AdminHeader />
      <div className="container">
        <h2>Reference Threads</h2>
        {topNegativeRecipients.length > 0 && (
          <div className="panel panel-default">
            <div className="panel-body">
              <h4>Top score from the last year</h4>
              <ol className="list-inline">
                {topNegativeRecipients.map(({ count, user }) => {
                  const userId = typeof user === 'string' ? user : user._id;
                  return (
                    <li key={userId}>
                      <span className="label label-danger">{count}</span>{' '}
                      <ReferenceUser user={user} />
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        )}
        {isFetching && <p>Loading reference threads...</p>}
        {!isFetching && referenceThreads.length > 0 && (
          <table className="table table-condensed table-striped">
            <thead>
              <tr>
                <th>When</th>
                <th>By</th>
                <th>For</th>
                <th>Messages</th>
              </tr>
            </thead>
            <tbody>
              {referenceThreads.map(({ _id, userFrom, userTo, created }) => {
                const messagesHref = getAdminMessagesHref(userTo, userFrom);

                return (
                  <tr key={_id}>
                    <td>
                      <time dateTime={getAdminDateTimeAttribute(created)}>
                        {formatAdminDateTime(created)}
                      </time>
                    </td>
                    <td>
                      <ReferenceUser user={userFrom} />
                    </td>
                    <td>
                      <ReferenceUser user={userTo} />
                    </td>
                    <td>
                      {messagesHref ? (
                        <a href={messagesHref}>See messages</a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
